// ===========================================================================
// WishlistConfig
// ===========================================================================
window.WishlistConfig = Object.assign({
  apiUrl: '',
  visitorCookieName: 'wl_visitor',
  wishlistCookieName: 'wl_ids',
  cookieMaxAge: 31536000
}, window.WishlistConfig || {});

// ===========================================================================
// WishlistCookie
// ===========================================================================
const WishlistCookie = {
  get(name) {
    const prefix = encodeURIComponent(name) + '=';
    const parts = document.cookie ? document.cookie.split('; ') : [];
    for (const part of parts) {
      if (part.indexOf(prefix) === 0) {
        try { return decodeURIComponent(part.substring(prefix.length)); }
        catch (e) { return null; }
      }
    }
    return null;
  },

  set(name, value, maxAge) {
    const age = typeof maxAge === 'number' ? maxAge : WishlistConfig.cookieMaxAge;
    document.cookie =
      encodeURIComponent(name) + '=' + encodeURIComponent(value) +
      '; path=/; max-age=' + age + '; SameSite=Lax';
  },

  _getEntries() {
    const raw = this.get(WishlistConfig.wishlistCookieName);
    if (!raw) return [];
    return raw.split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(s => {
        const parts = s.split(':');
        return { id: parts[0], handle: parts[1] || '' };
      })
      .filter(e => e.id);
  },

  _saveEntries(entries) {
    const value = entries.map(e => e.id + ':' + (e.handle || '')).join(',');
    this.set(WishlistConfig.wishlistCookieName, value);
  },

  getWishlistIds()      { return this._getEntries().map(e => e.id); },
  getWishlistEntries()  { return this._getEntries(); },

  addId(productId, handle) {
    const id = String(productId);
    const entries = this._getEntries();
    const existing = entries.find(e => e.id === id);
    if (!existing) {
      entries.push({ id, handle: handle || '' });
    } else if (handle && !existing.handle) {
      existing.handle = handle;
    }
    this._saveEntries(entries);
  },

  removeId(productId) {
    const id = String(productId);
    this._saveEntries(this._getEntries().filter(e => e.id !== id));
  },

  hasId(productId) {
    return this._getEntries().some(e => e.id === String(productId));
  }
};

// ===========================================================================
// WishlistAPI
// ===========================================================================
const WishlistAPI = {
  async _post(payload) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(WishlistConfig.apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      clearTimeout(timer);
      return res.ok ? await res.json() : null;
    } catch (e) {
      clearTimeout(timer);
      return null;
    }
  },

  async checkBatch(productIds) {
    if (!Array.isArray(productIds) || productIds.length === 0) return [];
    const data = await this._post({ action: 'check_batch', product_ids: productIds.map(String) });
    return (data && Array.isArray(data.liked_ids)) ? data.liked_ids : [];
  },

  async like(productId) {
    if (!productId) return null;
    const data = await this._post({ action: 'like', product_id: String(productId) });
    if (!data) return null;
    return {
      success: !!data.success,
      already_liked: !!data.already_liked,
      new_count: typeof data.new_count === 'number' ? data.new_count : null
    };
  },

  async unlike(productId) {
    if (!productId) return null;
    const data = await this._post({ action: 'unlike', product_id: String(productId) });
    if (!data) return null;
    return {
      success: !!data.success,
      not_liked: !!data.not_liked,
      new_count: typeof data.new_count === 'number' ? data.new_count : null
    };
  }
};

// ===========================================================================
// WishlistUI
// ===========================================================================
const WishlistUI = {

  init() {
    const hearts = Array.from(document.querySelectorAll('[data-wishlist-heart]'));
    if (hearts.length === 0) return;

    const ids = [];
    hearts.forEach(h => {
      const pid = h.getAttribute('data-product-id');
      if (pid && !ids.includes(pid)) ids.push(pid);
    });

    hearts.forEach(h => this.bindHeart(h));

    WishlistAPI.checkBatch(ids).then(likedIds => {
      const likedSet = new Set(likedIds);
      hearts.forEach(h => {
        const pid = h.getAttribute('data-product-id');
        const count = parseInt(h.getAttribute('data-count') || '0', 10) || 0;
        this.updateHeart(h, likedSet.has(pid), count);
      });
    });
  },

  updateHeart(heartEl, isLiked, count) {
    if (!heartEl) return;
    heartEl.setAttribute('data-liked', isLiked ? 'true' : 'false');
    heartEl.classList.toggle('is-liked', !!isLiked);

    const filled  = heartEl.querySelector('[data-heart-filled]');
    const outline = heartEl.querySelector('[data-heart-outline]');
    if (filled && outline) {
      filled.style.display  = isLiked ? '' : 'none';
      outline.style.display = isLiked ? 'none' : '';
    }

    const countEl = heartEl.querySelector('[data-wishlist-count]');
    if (countEl && typeof count === 'number' && !isNaN(count)) {
      countEl.textContent = String(count);
      heartEl.setAttribute('data-count', String(count));
    }
  },

  bindHeart(heartEl) {
    if (!heartEl || heartEl.__wishlistBound) return;
    heartEl.__wishlistBound = true;

    heartEl.addEventListener('click', async (ev) => {
      ev.preventDefault();
      ev.stopPropagation();

      const productId = heartEl.getAttribute('data-product-id');
      if (!productId) return;

      const wasLiked  = heartEl.getAttribute('data-liked') === 'true';
      const prevCount = parseInt(heartEl.getAttribute('data-count') || '0', 10) || 0;
      const handle    = heartEl.getAttribute('data-product-handle') || '';

      if (wasLiked) {
        // — Unlike —
        this.updateHeart(heartEl, false, Math.max(0, prevCount - 1));
        heartEl.setAttribute('disabled', 'disabled');

        const result = await WishlistAPI.unlike(productId);
        heartEl.removeAttribute('disabled');

        if (result && result.success) {
          WishlistCookie.removeId(productId);
          this.updateHeart(heartEl, false, result.new_count ?? Math.max(0, prevCount - 1));
          this.updateBadge();
        } else {
          this.updateHeart(heartEl, true, prevCount);
        }
        return;
      }

      // — Like —
      this.updateHeart(heartEl, true, prevCount + 1);
      heartEl.setAttribute('disabled', 'disabled');

      const result = await WishlistAPI.like(productId);
      heartEl.removeAttribute('disabled');

      if (result && result.success) {
        WishlistCookie.addId(productId, handle);
        this.updateHeart(heartEl, true, result.new_count ?? prevCount + 1);
        document.dispatchEvent(new CustomEvent('wishlist:liked', { detail: { productId } }));
        this.updateBadge();
      } else if (result && result.already_liked) {
        WishlistCookie.addId(productId, handle);
        this.updateHeart(heartEl, true, prevCount);
      } else {
        this.updateHeart(heartEl, false, prevCount);
      }
    });
  },

  updateBadge() {
    const count = WishlistCookie.getWishlistIds().length;
    document.querySelectorAll('.wl-nav-badge, [data-wishlist-badge]').forEach(el => {
      el.textContent = String(count);
      el.setAttribute('data-count', String(count));
      el.classList.toggle('visible', count > 0);
      el.classList.toggle('is-empty', count === 0);
      el.style.display = count > 0 ? '' : 'none';
    });
  },

  renderDrawerProduct(product) {
    const id     = product.id;
    const title  = this._escape(product.title || '');
    const handle = this._escape(product.handle || '');

    let imageSrc = '';
    if (product.featured_image) {
      imageSrc = typeof product.featured_image === 'string'
        ? product.featured_image : (product.featured_image.src || '');
    }
    if (!imageSrc && Array.isArray(product.images) && product.images[0]) {
      imageSrc = typeof product.images[0] === 'string'
        ? product.images[0] : (product.images[0].src || '');
    }

    const variant = (product.variants && product.variants[0]) || {};
    const price   = this._formatMoney(variant.price);
    const imgHtml = imageSrc
      ? '<img src="' + this._escape(imageSrc) + '" alt="' + title + '" loading="lazy">'
      : '';

    return (
      '<div class="wl-product-card" data-product-id="' + this._escape(id) + '">' +
        imgHtml +
        '<div class="wl-product-card__info">' +
          '<div class="wl-product-card__title">' + title + '</div>' +
          '<div class="wl-product-card__price">' + price + '</div>' +
          '<div class="wl-product-card__actions">' +
            '<a href="/products/' + handle + '" class="btn btn--small">View product</a>' +
            '<button class="btn btn--small btn--secondary wl-remove-btn" data-product-id="' + this._escape(id) + '">Remove</button>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  },

  _escape(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  },

  _formatMoney(cents) {
    const value = typeof cents === 'string' ? parseFloat(cents) : Number(cents);
    if (isNaN(value)) return '';
    const amount = (typeof cents === 'string' && cents.includes('.')) ? value : value / 100;
    let symbol = '€';
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active) {
        const symbols = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$', JPY: '¥' };
        symbol = symbols[Shopify.currency.active] || (Shopify.currency.active + ' ');
      }
    } catch (e) {}
    return symbol + amount.toFixed(2);
  }
};

// ===========================================================================
// Drawer product loader (uses cart drawer's WlWishlistBody)
// ===========================================================================
WishlistUI.loadDrawerProducts = async function () {
  const list = document.getElementById('wishlist-drawer')?.querySelector('[data-wishlist-list]')
            || document.querySelector('.wl-drawer-body');
  if (!list) return;

  const entries = WishlistCookie.getWishlistEntries();
  if (entries.length === 0) {
    list.innerHTML = '<p class="wl-drawer-empty">Your wishlist is empty</p>';
    WishlistUI.updateBadge();
    return;
  }

  const fetches = entries
    .filter(e => e.handle)
    .map(e =>
      fetch('/products/' + encodeURIComponent(e.handle) + '.js', { credentials: 'same-origin' })
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    );

  const products = (await Promise.allSettled(fetches))
    .map(s => s.status === 'fulfilled' ? s.value : null)
    .filter(p => p && p.id);

  if (products.length === 0) {
    list.innerHTML = '<p class="wl-drawer-empty">Your wishlist is empty</p>';
    WishlistUI.updateBadge();
    return;
  }

  list.innerHTML = products.map(p => WishlistUI.renderDrawerProduct(p)).join('');

  list.querySelectorAll('.wl-remove-btn').forEach(btn => {
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      WishlistUI.removeFromDrawer(btn.getAttribute('data-product-id'));
    });
  });

  WishlistUI.updateBadge();
};

WishlistUI.removeFromDrawer = function (productId) {
  if (!productId) return;
  const id = String(productId);

  WishlistCookie.removeId(id);

  const card = document.querySelector('.wl-product-card[data-product-id="' + CSS.escape(id) + '"]');
  if (card) card.remove();

  WishlistAPI.unlike(id).then(result => {
    const newCount = result?.new_count ?? null;
    document.querySelectorAll('[data-wishlist-heart][data-product-id="' + CSS.escape(id) + '"]')
      .forEach(h => {
        const c = newCount != null
          ? newCount
          : Math.max(0, parseInt(h.getAttribute('data-count') || '0', 10) - 1);
        WishlistUI.updateHeart(h, false, c);
      });
  });

  WishlistUI.updateBadge();
  document.dispatchEvent(new CustomEvent('wishlist:removed', { detail: { productId: id } }));
};

// ===========================================================================
// Expose globals
// ===========================================================================
window.WishlistCookie = WishlistCookie;
window.WishlistAPI    = WishlistAPI;
window.WishlistUI     = WishlistUI;

// ===========================================================================
// Init
// ===========================================================================
document.addEventListener('DOMContentLoaded', () => {
  WishlistUI.init();
  WishlistUI.updateBadge();
});

document.addEventListener('shopify:section:load', () => WishlistUI.init());
document.addEventListener('wishlist:liked',   () => WishlistUI.updateBadge());
document.addEventListener('wishlist:removed', () => WishlistUI.updateBadge());
document.addEventListener('wishlist:updated', () => WishlistUI.updateBadge());